import React, { useEffect, useState } from 'react';
import { ClipboardList, CheckCircle2, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';

const API = '';

export const StudentActivities = ({ subject, token }) => {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = () => fetch(`${API}/api/activities/student/items?token=${token}&subject=${encodeURIComponent(subject)}`)
    .then((r) => (r.ok ? r.json() : { items: [] })).then((d) => setItems(d.items || [])).catch(() => setItems([]));
  useEffect(() => { if (subject && token) load(); }, [subject, token]);

  if (!items || items.length === 0) return null;

  return (
    <div className="mb-6 rounded-xl border border-violet-200 bg-violet-50/40 p-4" data-testid="student-activities">
      <div className="flex items-center gap-2 mb-1"><ClipboardList className="h-5 w-5 text-violet-700" /><h3 className="font-semibold text-violet-900">Activities & Chapter Tests — {subject}</h3></div>
      <p className="text-sm text-violet-800/80 mb-3">Answer online. Written answers are marked automatically against the answer key; hands-on parts are marked by your teacher.</p>
      <div className="grid gap-2 md:grid-cols-2">
        {items.map((it) => (
          <button key={it.id} type="button" onClick={() => setOpenId(it.id)} data-testid={`student-activity-${it.code}`}
            className="text-left rounded-lg border bg-white px-4 py-3 hover:shadow-md hover:border-violet-300 transition-all flex items-center gap-3">
            <span className={`h-9 w-9 rounded-md text-white font-bold text-xs flex items-center justify-center ${it.kind === 'test' ? 'bg-rose-500' : 'bg-violet-600'}`}>{it.code}</span>
            <span className="flex-1 min-w-0">
              <span className="block font-medium text-slate-900 truncate">{it.title}</span>
              <span className="block text-xs text-slate-500">{it.n_questions} questions · {it.total_marks} marks</span>
            </span>
            {it.sub_status ? (
              <Badge className="bg-emerald-600" data-testid={`student-activity-score-${it.code}`}><CheckCircle2 className="h-3 w-3 mr-1" />{Number(it.score)}/{it.total_marks}</Badge>
            ) : <Badge variant="outline" className="border-violet-300 text-violet-700">Start</Badge>}
          </button>
        ))}
      </div>
      {openId && <ActivityModal itemId={openId} token={token} onClose={() => { setOpenId(null); load(); }} />}
    </div>
  );
};

function ActivityModal({ itemId, token, onClose }) {
  const [item, setItem] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/activities/student/items/${itemId}?token=${token}`).then((r) => r.json()).then((d) => {
      setItem(d);
      if (d.submission) { setAnswers(d.submission.answers || {}); setResult({ ...d.submission, questions: d.questions }); }
    });
  }, [itemId, token]);

  const submit = async () => {
    const blank = (item.questions || []).filter((q) => !String(answers[q.q_num] || '').trim()).length;
    if (blank && !window.confirm(`${blank} question(s) are blank. Submit anyway? You can only submit once.`)) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/activities/student/items/${itemId}/submit?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ answers }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not submit');
      setResult(d);
      toast.success(`Submitted — ${d.score}/${d.total_marks}`);
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="student-activity-modal">
        {!item ? <Loader2 className="h-6 w-6 animate-spin text-violet-600" /> : (
          <>
            <DialogHeader>
              <DialogTitle>{item.code} · {item.title}</DialogTitle>
              <DialogDescription>{item.instructions} · {item.total_marks} marks</DialogDescription>
            </DialogHeader>
            {result && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-4" data-testid="student-activity-result">
                <span className="text-3xl font-bold text-emerald-700">{Number(result.score)}/{result.total_marks}</span>
                <div className="text-sm text-emerald-900">
                  <p className="font-medium">{Math.round(result.percentage)}% {result.status === 'submitted' ? '· hands-on parts still to be marked by your teacher' : result.status === 'published' ? '· in your Grade Book' : '· marked (your teacher may adjust)'}</p>
                  <p className="text-xs">Read the feedback under each question.</p>
                </div>
              </div>
            )}
            <div className="space-y-4">
              {(item.questions || []).map((q) => {
                const m = result?.marks?.[String(q.q_num)];
                return (
                  <div key={q.q_num} className="rounded-lg border p-3 space-y-2" data-testid={`student-q-${q.q_num}`}>
                    <p className="text-sm font-medium text-slate-900">
                      {q.part && <span className="text-xs uppercase text-violet-600 mr-2">{q.part}</span>}
                      Q{q.q_num}. {q.text} <span className="text-slate-400 font-normal">[{q.marks} marks]</span>
                    </p>
                    {q.type === 'mcq' || q.type === 'truefalse' ? (
                      <div className="flex flex-wrap gap-2">
                        {(q.type === 'truefalse' ? ['True', 'False'] : q.options || []).map((opt) => (
                          <button key={opt} type="button" disabled={!!result} onClick={() => setAnswers((a) => ({ ...a, [q.q_num]: opt }))}
                            className={`text-sm px-3 py-1.5 rounded-md border ${answers[q.q_num] === opt ? 'bg-violet-600 text-white border-violet-600' : 'bg-white hover:bg-slate-50'}`}>{opt}</button>
                        ))}
                      </div>
                    ) : (
                      <Textarea rows={q.type === 'practical' ? 4 : 3} disabled={!!result} value={answers[q.q_num] || ''}
                        onChange={(e) => setAnswers((a) => ({ ...a, [q.q_num]: e.target.value }))}
                        placeholder={q.type === 'practical' ? 'Record what you did and found (your teacher marks this part)…' : 'Type your answer in full sentences…'}
                        className="text-sm" data-testid={`student-answer-${q.q_num}`} />
                    )}
                    {m && (
                      <p className={`text-xs rounded px-2 py-1 ${m.by === 'pending' ? 'bg-amber-50 text-amber-800' : 'bg-slate-50 text-slate-700'}`}>
                        <span className="font-semibold">{m.by === 'pending' ? 'Teacher to mark' : `${m.score}/${q.marks}`}</span> — {m.feedback}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            {!result && (
              <div className="flex justify-end">
                <Button className="bg-violet-600 hover:bg-violet-700" disabled={submitting} onClick={submit} data-testid="student-activity-submit">
                  {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}{submitting ? 'Marking…' : 'Submit answers'}
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
